/**
 * Owner: Masa Bayu
 * File: PrivateAuthorBooks.tsx
 * Date:Time: 2026-10-23 08:50
 * Description: Karusel Buku Saya §467-C — HANYA terbitan milik akaun penerbit
 *   (`publications` daripada /v1/publisher/dashboard); katalog pasar tidak lagi
 *   dipaparkan sebagai "buku saya". Menu ⋮ → butiran (/publication/) + pembaca
 *   (/read/) kedua-dua laluan hidup. Lencana = 'Tersedia' jika ada harga, jika
 *   tidak status jujur 'Harga belum ditetapkan' — bukan 'Published' automatik.
 */

import { useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { LandingIcon } from '../../home/marketplace/landing/LandingIcons';
import type { MarketplaceLanguage } from '../../home/marketplace/marketplace-locale';
import { qxCategoryName } from '@qx/qxread-core';
import type { AuthorBookRow } from './private-author-view';
import type { PrivateAuthorLabels } from './private-author-labels';
import { removeOwnedPublication } from './publisher-dashboard';

const TONES = ['#dbeafe', '#fde8e8', '#e7f6ef', '#fef3c7', '#f3e8ff'];

export interface PrivateAuthorBooksProps {
  readonly onNavigate: (path: string) => void;
  readonly la: PrivateAuthorLabels;
  readonly books: readonly AuthorBookRow[];
  readonly language: MarketplaceLanguage;
}

export function PrivateAuthorBooks({
  onNavigate,
  la,
  books,
  language,
}: PrivateAuthorBooksProps): ReactElement {
  const railRef = useRef<HTMLDivElement>(null);
  const [dot, setDot] = useState(0);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const removeBook = (book: AuthorBookRow): void => {
    const question = la.bookMenuRemoveConfirm.replace('{title}', book.title);
    if (!window.confirm(question)) return;
    setBusyId(book.publicationId);
    setNotice(null);
    void removeOwnedPublication(book.publicationId).then((result) => {
      setBusyId(null);
      setMenuFor(null);
      if (!result.ok) setNotice(result.error);
    });
  };

  const scrollBy = (dir: 1 | -1): void => {
    const rail = railRef.current;
    if (rail === null) {
      return;
    }
    rail.scrollBy({ left: dir * Math.round(rail.clientWidth * 0.8), behavior: 'smooth' });
  };

  const onScroll = (): void => {
    const rail = railRef.current;
    if (rail === null) {
      return;
    }
    const span = rail.scrollWidth - rail.clientWidth;
    setDot(span <= 0 ? 0 : Math.min(3, Math.round((rail.scrollLeft / span) * 3)));
  };

  const goToDot = (i: number): void => {
    const rail = railRef.current;
    if (rail === null) {
      return;
    }
    const span = rail.scrollWidth - rail.clientWidth;
    rail.scrollTo({ left: (span * i) / 3, behavior: 'smooth' });
  };

  return (
    <section className="qxp__card qxp__books" aria-label={la.booksTitle}>
      <div className="qxp__card-head">
        <h2 className="qxp__card-title">{la.booksTitle}</h2>
        <div className="qxp__card-tools">
          <button
            type="button"
            className="qxp__seeall"
            onClick={() => {
              onNavigate('/author/publications');
            }}
          >
            {la.seeAll}
          </button>
          <button
            type="button"
            className="qxp__carbtn"
            aria-label={la.carouselPrevAria}
            onClick={() => {
              scrollBy(-1);
            }}
          >
            <LandingIcon name="chevronLeft" size={14} />
          </button>
          <button
            type="button"
            className="qxp__carbtn"
            aria-label={la.carouselNextAria}
            onClick={() => {
              scrollBy(1);
            }}
          >
            <LandingIcon name="chevronRight" size={14} />
          </button>
        </div>
      </div>
      {notice === null ? null : (
        <p className="qxp__empty" role="alert">
          {notice}
        </p>
      )}
      {books.length === 0 ? (
        <p className="qxp__empty">{la.booksEmpty}</p>
      ) : (
        <>
          <div className="qxp__bookrail" ref={railRef} onScroll={onScroll}>
            {books.map((book) => (
              <article className="qxp__book" key={book.publicationId}>
                <div className="qxp__book-top">
                  <span className={`qxp__chip${book.listed ? '' : ' qxp__chip--hot'}`}>
                    {book.listed ? la.status.published : la.status.unlisted}
                  </span>
                  <button
                    type="button"
                    className="qxp__menubtn"
                    aria-label={`${la.bookMenuAria} — ${book.title}`}
                    aria-expanded={menuFor === book.publicationId}
                    onClick={() => {
                      setMenuFor(menuFor === book.publicationId ? null : book.publicationId);
                    }}
                  >
                    <LandingIcon name="more" size={16} />
                  </button>
                  {menuFor === book.publicationId ? (
                    <div className="qxp__menu" role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setMenuFor(null);
                          onNavigate(`/publication/${encodeURIComponent(book.publicationId)}`);
                        }}
                      >
                        {la.bookMenuDetail}
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setMenuFor(null);
                          onNavigate(`/read/${encodeURIComponent(book.publicationId)}`);
                        }}
                      >
                        {la.bookMenuRead}
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        disabled={busyId === book.publicationId}
                        onClick={() => {
                          removeBook(book);
                        }}
                      >
                        {la.bookMenuRemove}
                      </button>
                    </div>
                  ) : null}
                </div>
                <div
                  className="qxp__book-cover"
                  style={{ background: TONES[book.coverTone % TONES.length] ?? '#dbeafe' }}
                >
                  <span>{book.title}</span>
                </div>
                <h3 className="qxp__book-title">{book.title}</h3>
                {book.category === null ? null : (
                  <p className="qxp__book-author">{qxCategoryName(book.category, language)}</p>
                )}
                <div className="qxp__book-foot">
                  <strong>{book.priceText}</strong>
                  {book.soldOrders > 0 ? (
                    <span className="qxp__book-sold">
                      {`${String(book.soldOrders)} ${la.soldSuffix}`}
                    </span>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
          <div className="qxp__dots" aria-hidden="true">
            {[0, 1, 2, 3].map((i) => (
              <button
                key={i}
                type="button"
                className={`qxp__dot${dot === i ? ' qxp__dot--on' : ''}`}
                onClick={() => {
                  goToDot(i);
                }}
                tabIndex={-1}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
